/**
 * Конвейер — API-клиент.
 *
 * Backend: design-service через gateway (:3030).
 * Формат ответа gateway: { success: boolean, data: T } | { success: false, error: { code, message } }.
 */

import { api } from '../../shared/api/client'
import type { FunnelCoverage } from './funnelCoverage'

export interface PipelineListParams {
  stage?: string
  theme?: string
  search?: string
  limit?: number
  offset?: number
}

export interface ApproveDesignOptions {
  publishConfirmed: boolean
  dryRun?: boolean
  expectedRunId?: string
}

export interface MarketplacePublicationAggregate {
  processed: number
  published: number
  failed: number
  already_published: number
  [key: string]: unknown
}

export type MarketplacePublicationOutcome =
  | 'published'
  | 'partial'
  | 'already_published'
  | 'preview'
  | 'success'

export interface MarketplacePublicationReconciliation {
  failed?: number
  errors?: string[]
  [key: string]: unknown
}

/** Нормализованный итог лежит сверху; publish остаётся сырым ответом адаптера WB. */
export interface ApproveDesignResult {
  outcome?: MarketplacePublicationOutcome
  counts?: MarketplacePublicationAggregate
  reconciliation?: MarketplacePublicationReconciliation | null
  publish?: Record<string, unknown> | null
  preview?: unknown
  /** Legacy rolling-deploy ответ до нормализованного outcome. */
  published?: number
  [key: string]: unknown
}

export interface RetryDesignResult {
  step: string
  result: unknown | null
  requires_confirmation?: true
  confirmation?: 'marketplace_publication'
  [key: string]: unknown
}

/** DTO строки из GET /api/v1/pipeline (см. PipelineRepository.findPipeline) */
export interface PipelineDesignDto {
  design_code: string
  theme_number: number
  theme_name?: string | null
  type: string
  category?: string | null
  brand_ru?: string | null
  image_text?: string | null
  theme?: string | null
  pipeline_status: string
  pipeline_error?: string | null
  tech_check?: unknown
  seo_status?: string | null
  accepted_at?: string | null
  status_changed_at?: string | null
  created_at?: string | null
  updated_at?: string | null
  product_count: number
  wb_product_count: number
  /** Превью макета (base_media, is_main) — относительный URL для миниатюры в списке */
  preview_url?: string | null
  /** Товары 1С с превью визуалов — backend отдаёт для stage=on_approval и sales-стадий */
  products?: { article: string; image_urls: string[] }[]
}

export interface PipelineListResponse {
  items: PipelineDesignDto[]
  total: number
  counts: { by_stage: Record<string, number> }
}

export interface PipelineDesignDetailsDto {
  design_code: string
  theme_number: number
  type: string
  category: string
  brand_ru: string
  brand_en?: string | null
  design_name?: string | null
  gender: string
  age?: string | null
  age_on_image?: number | null
  age_on_image_unit?: string | null
  name_on_image?: string | null
  profession?: string | null
  image_subject?: string | null
  image_text?: string | null
  occasion?: string | null
  recipient_statuses?: string[]
  seasonal?: number | null
  holiday_date?: string | null
  language?: string | null
  theme?: string | null
  emotion?: string | null
  legal_status?: string | null
  design_state?: string | null
  lifecycle_flag?: string | null
  season?: string | null
  design_type?: string | null
  nomenclature_type?: string | null
  completeness?: unknown
  pipeline_status?: string | null
  pipeline_error?: string | null
  tech_check?: unknown
  seo_status?: string | null
  accepted_at?: string | null
  status_changed_at?: string | null
  user_id?: number | null
  owner_id?: number | null
  created_at?: string | null
  updated_at?: string | null
  deleted_at?: string | null
  media?: Array<{
    id: number
    url?: string | null
    type?: string | null
    isMain?: number | boolean | null
    is_main?: number | boolean | null
    fileName?: string | null
    file_name?: string | null
  }>
  [key: string]: unknown
}

export interface MarketplaceCardRevisionSnapshotDto {
  id: number
  revision: number
  run_id: string
  seo_content_id: number | null
  seo_revision_id: number | null
  seo_revision: number | null
  target: Record<string, unknown> | null
  asset_manifest: Record<string, unknown> | null
  title: string | null
  description: string | null
  characteristics: Record<string, unknown> | null
  category_id: number | null
  category_prefix: string | null
  marketplace_category: Record<string, unknown> | null
  technical: Record<string, unknown> | null
  attributes: Record<string, unknown> | null
  /**
   * Готовность, посчитанная при заморозке черновика: `blocking` — публикация
   * будет отклонена, `gaps` — уедет с пустым полем. Коды приходят машинные,
   * человеческие формулировки живут в `marketplaceReasons.ts`.
   */
  readiness?: {
    publishable: boolean
    blocking: string[]
    gaps: string[]
  } | null
  /** Замороженные цена и остаток той площадки, на которую едет карточка. */
  commercials?: Record<string, unknown> | null
  /** Сколько изображений вошло в ревизию и код отказа визуалов, если он был. */
  assets?: { photos: number | null; rejection: string | null } | null
  /**
   * Значения полей карточки по ключам справочника характеристик; подписи к ним
   * приходят в `field_labels` ответа.
   */
  field_values?: Record<string, string | number | boolean | null>
}

/** Состояние карточки; выводится в design-service, своей колонки в БД не имеет. */
export type MarketplaceCardState =
  | 'live'
  | 'live_no_photo'
  | 'commercial_pim'
  | 'error'
  | 'frozen'
  | 'no_revision'

/** Подпись поля карточки в справочнике площадки (`mp_characteristic_mappings`). */
export interface MarketplaceFieldLabel {
  key: string
  name: string
  required: boolean
}

export type MarketplaceFieldLabels = Record<'wb' | 'ozon' | 'ym', MarketplaceFieldLabel[]>

export interface MarketplaceCardDto {
  id: number
  article: string
  design_code: string
  /** Площадка карточки либо канал сайта: бэкенд отдаёт на вкладку и карточки сайта. */
  marketplace: 'wb' | 'ozon' | 'ym' | 'site'
  seller_id: number
  seller_name?: string | null
  external_id?: string | null
  /**
   * Карточка ПИМ (`marketplace_cards.id`), связанная со строкой. Есть у строк
   * зеркал; `null` — зеркало без карточки ПИМ. Поля нет вовсе, если сервис старой
   * версии: тогда `id` строки-черновика и есть `marketplace_cards.id`.
   */
  pim_card_id?: number | null
  /** Строка зеркала площадки (`wb_products.nmID`, `ozon_products.id`, `ym_products.id`); у черновика — `null`. */
  mirror_id?: number | null
  /**
   * У WB/OZON — легаси-метка PIM из небольшого набора значений ниже. У Я.Маркета
   * design-marketplace-cards.service отдаёт РЕАЛЬНЫЙ статус площадки
   * (`card_status`, 9 значений Partner API — словарь в
   * `features/yandex-market/catalog/columns.tsx`), поэтому тип не сужаем до
   * литералов: строка ym в это множество не входит.
   */
  status: 'creating' | 'created' | 'price_set' | 'stock_set' | 'selling' | 'error' | string
  /** Читаемое состояние карточки; см. MarketplaceCardState. */
  state?: MarketplaceCardState
  price?: number | null
  stock?: number | null
  sales_enabled_at?: string | null
  error?: string | null
  created_at?: string | null
  updated_at?: string | null
  title?: string | null
  category_prefix?: string | null
  category_description?: string | null
  preview_url?: string | null
  image_urls?: string[]
  revision_snapshot?: MarketplaceCardRevisionSnapshotDto | null
}

export interface MarketplaceCardsResponse {
  items: MarketplaceCardDto[]
  total: number
  counts: { by_status: Record<string, number>; by_state?: Record<string, number> }
  /** Подписи полей карточки по площадкам; пустые, если справочник недоступен. */
  field_labels?: MarketplaceFieldLabels
}

export interface PipelineRunStatusDto {
  run_id: string
  design_code: string
  design_type?: string | null
  status: 'planning' | 'generating' | 'completing' | 'ready' | 'failed' | string
  error?: string | null
  pipeline_status?: string | null
  pipeline_error?: string | null
  products_total: number
  products_ready?: number
  production_ready: number
  targets_total: number
  targets_ready: number
  targets_failed: number
  targets_pending: number
  cards_created: number
  seo_ready?: number
  terminal: boolean
  target_errors: Array<{
    article: string
    marketplace: string
    seller_id: number
    cabinet?: string | null
    severity?: 'warning' | 'error'
    error?: string | null
  }>
  seo_excluded?: Array<{
    article: string
    marketplace: string
    seller_id: number
    cabinet?: string | null
    reason: string
    reasons?: string[]
  }>
}

/** Ответ приёмки макета (design-service, AcceptPipelineDesignUseCase). */
export interface AcceptDesignResult {
  run_id?: string
  /** pipeline_status макета после приёмки. */
  status?: string
  deduplicated?: boolean
  /** Позиции, которым карточка маркетплейса не достанется, и почему. */
  funnel_coverage?: FunnelCoverage
  [key: string]: unknown
}

/** Ответ пакетного заведения карточек сайта: отбор посчитан, прогон ушёл в фон. */
export interface CreateSiteCardsResult {
  jobId: string
  /** Машинный код кабинета сайта, под который заводятся карточки. */
  cabinet: string
  /** Сколько макетов пойдёт в прогон. */
  designs_total: number
  articles_requested: number
  articles_planned: number
  /** Что отобрано не будет и почему. */
  skipped: {
    already_has_card: number
    no_design: number
    not_found: number
  }
}

/** Категория матрицы «тип макета → товары» для шага выбора товаров. */
export interface RegenerationPlanCategory {
  category_id: number
  category_prefix: string
  category_description: string
  /** Сколько товаров положено по матрице. */
  quantity: number
  /** Уже созданные артикулы. */
  articles: string[]
  /** Сколько товаров матрица ждёт, а их ещё нет — создадутся при запуске. */
  missing: number
  required_generation_mode: 'single' | 'multi'
}

export interface RegenerationPlanCabinet {
  marketplace: string
  seller_id: number
  cabinet: string
  name: string
  /** Кабинет уже участвовал в прогоне этого макета. */
  used_before: boolean
}

export interface RegenerationPlan {
  design_code: string
  design_type: string
  categories: RegenerationPlanCategory[]
  cabinets: RegenerationPlanCabinet[]
}

export interface RegenerationFunnelPlan {
  canonical_targets: Array<{ marketplace: string; seller_id: number; cabinet: string }>
  rows: any[]
  funnels: any[]
}

/** Элемент пакетного ответа: результат макета или его ошибка. */
export interface RegenerationBatchItem<T> {
  design_code: string
  result?: T
  error?: { code: string; message: string }
}

const normalizeRegenerationPlan = (
  data: Partial<RegenerationPlan> | undefined,
  designCode: string,
): RegenerationPlan => ({
  design_code: String(data?.design_code || designCode),
  design_type: String(data?.design_type || ''),
  categories: Array.isArray(data?.categories) ? data.categories : [],
  cabinets: Array.isArray(data?.cabinets) ? data.cabinets : [],
})

const normalizeRegenerationFunnelPlan = (
  data: Partial<RegenerationFunnelPlan> | undefined,
): RegenerationFunnelPlan => ({
  canonical_targets: Array.isArray(data?.canonical_targets) ? data.canonical_targets : [],
  rows: Array.isArray(data?.rows) ? data.rows : [],
  funnels: Array.isArray(data?.funnels) ? data.funnels : [],
})

function normalizeBatchItems<T>(
  data: any,
  normalize: (result: any, designCode: string) => T,
): Array<RegenerationBatchItem<T>> {
  const items: any[] = Array.isArray(data?.items) ? data.items : []
  return items.map((item) => {
    const code = String(item?.design_code || '')
    if (item?.error) {
      return {
        design_code: code,
        error: {
          code: String(item.error.code || 'INTERNAL_ERROR'),
          message: String(item.error.message || 'Ошибка макета'),
        },
      }
    }
    return { design_code: code, result: normalize(item?.result, code) }
  })
}

/** Закреплённая человеком воронка для пары «категория × кабинет». */
export interface RegenerationFunnelSelection {
  category_id: number
  marketplace: string
  seller_id: number
  cabinet: string
  version_id: number
  rule_id: number
  source?: 'auto' | 'manual'
}

export interface RegenerateSelectedVisualsResult {
  job_id: string
  run_id: string
  design_code: string
  targets: Array<{ marketplace: string; seller_id: number; cabinet: string }>
  media_jobs: number
  production_jobs: number
}

function unwrap<T>(response: any, fallback: T): T {
  return response.data?.data ?? response.data ?? fallback
}

export const pipelineAPI = {
  async getRunStatuses(runIds: string[]): Promise<PipelineRunStatusDto[]> {
    const ids = [...new Set(runIds.map(String).map((id) => id.trim()).filter(Boolean))]
    const requests: Array<Promise<PipelineRunStatusDto[]>> = []
    for (let index = 0; index < ids.length; index += 50) {
      const chunk = ids.slice(index, index + 50)
      requests.push(api.post('/pipeline/runs/status', { run_ids: chunk }).then((response) => {
        const data = unwrap<{ runs?: PipelineRunStatusDto[] }>(response, {})
        return Array.isArray(data.runs) ? data.runs : []
      }))
    }
    return (await Promise.all(requests)).flat()
  },

  async getPipeline(params: PipelineListParams = {}): Promise<PipelineListResponse> {
    const response = await api.get('/pipeline', { params })
    const data = unwrap<Partial<PipelineListResponse>>(response, {})
    return {
      items: Array.isArray(data.items) ? data.items : [],
      total: Number(data.total) || 0,
      counts: { by_stage: data.counts?.by_stage ?? {} },
    }
  },

  /**
   * ВНИМАНИЕ: accept — реальное действие. В режиме designs_and_products (по
   * умолчанию) создаёт товары 1С, переводит статус макета и ставит в очередь
   * файлы для печати. Визуалы и карточки маркетплейсов при этом НЕ создаются:
   * им нужен явный выбор кабинетов, а он есть только в мастере «Создать
   * макеты» — оттуда приходит full_pipeline.
   *
   * В ответе приходит `funnel_coverage` — категории, которые карточку не
   * получат, и почему (см. funnelCoverage.ts). Пропуск таких позиций молчит
   * везде, кроме этого поля.
   */
  async acceptDesign(
    designCode: string,
    options?: { mode?: 'designs_and_products' | 'full_pipeline' }
  ): Promise<AcceptDesignResult> {
    const response = await api.post(`/pipeline/${encodeURIComponent(designCode)}/accept`, {
      mode: options?.mode ?? 'designs_and_products',
    })
    return unwrap<AcceptDesignResult>(response, response.data)
  },

  async returnDesign(designCode: string, comment: string): Promise<any> {
    const response = await api.post(`/pipeline/${encodeURIComponent(designCode)}/return`, { comment })
    return unwrap(response, response.data)
  },

  async approveDesign(
    designCode: string,
    options: ApproveDesignOptions,
  ): Promise<ApproveDesignResult> {
    const response = await api.post(`/pipeline/${encodeURIComponent(designCode)}/approve`, {
      publish_confirmed: options?.publishConfirmed === true,
      ...(typeof options?.dryRun === 'boolean' ? { dry_run: options.dryRun } : {}),
      ...(options?.expectedRunId?.trim()
        ? { expected_run_id: options.expectedRunId.trim() }
        : {}),
    })
    return unwrap(response, response.data)
  },

  /** Принудительный перезапуск генерации визуалов (без требования auto_generate) */
  async regenerateDesign(designCode: string): Promise<any> {
    const response = await api.post(`/pipeline/${encodeURIComponent(designCode)}/regenerate`, {})
    return unwrap(response, response.data)
  },

  /**
   * Только файлы печати: визуалы и черновики карточек не трогаются, макет
   * возвращается в ту же стадию, из которой запустили.
   */
  async regenerateProductionFiles(designCode: string): Promise<any> {
    const response = await api.post(
      `/pipeline/${encodeURIComponent(designCode)}/regenerate-production-files`,
      {},
    )
    return unwrap(response, response.data)
  },

  /** Повтор упавшего шага; publication/commercials требуют нового подтверждения человека. */
  async retryDesign(designCode: string): Promise<RetryDesignResult> {
    const response = await api.post(`/pipeline/${encodeURIComponent(designCode)}/retry`, {})
    return unwrap(response, response.data)
  },

  /** Перегенерация ОДНОЙ воронки: товар × кабинет */
  async regenerateFunnel(designCode: string, article: string, cabinet: string): Promise<any> {
    const response = await api.post(`/pipeline/${encodeURIComponent(designCode)}/regenerate-funnel`, { article, cabinet })
    return unwrap(response, response.data)
  },

  /**
   * Справочники мастера «Перегенерировать визуалы»: товары по матрице типа
   * макета и активные кабинеты с отметкой «уже был в прогоне этого макета».
   */
  async getRegenerationPlan(designCode: string): Promise<RegenerationPlan> {
    const response = await api.get(
      `/pipeline/${encodeURIComponent(designCode)}/regeneration-plan`,
    )
    return normalizeRegenerationPlan(unwrap<Partial<RegenerationPlan>>(response, {}), designCode)
  },

  /** План контент-воронок по выбранным категориям и кабинетам макета. */
  async planRegenerationFunnels(
    designCode: string,
    categoryIds: number[],
    targets: Array<{ marketplace: string; seller_id: number }>,
  ): Promise<RegenerationFunnelPlan> {
    const response = await api.post(
      `/pipeline/${encodeURIComponent(designCode)}/regeneration-funnels`,
      { category_ids: categoryIds, targets },
    )
    return normalizeRegenerationFunnelPlan(unwrap<Partial<RegenerationFunnelPlan>>(response, {}))
  },

  /**
   * Планы пачки макетов одним запросом — для «Пересоздать карточки» по выборке.
   * Запрос на макет упирался в лимит nginx (10 в секунду) уже на третьем десятке.
   * Ошибка одного макета приходит в его элементе, остальные посчитаны.
   */
  async getRegenerationPlansBatch(
    designCodes: string[],
  ): Promise<Array<RegenerationBatchItem<RegenerationPlan>>> {
    const response = await api.post('/pipeline/regeneration-plans/batch', {
      design_codes: designCodes,
    })
    return normalizeBatchItems(unwrap<any>(response, {}), (result, code) =>
      normalizeRegenerationPlan(result, code),
    )
  },

  /** План контент-воронок пачки макетов: категории у каждого свои, кабинеты общие. */
  async planRegenerationFunnelsBatch(
    designs: Array<{ design_code: string; category_ids: number[] }>,
    targets: Array<{ marketplace: string; seller_id: number }>,
  ): Promise<Array<RegenerationBatchItem<RegenerationFunnelPlan>>> {
    const response = await api.post('/pipeline/regeneration-funnels/batch', { designs, targets })
    return normalizeBatchItems(unwrap<any>(response, {}), (result) =>
      normalizeRegenerationFunnelPlan(result),
    )
  },

  /**
   * Запуск перегенерации визуалов по выбору человека.
   *
   * jobId создаёт бэкенд и возвращает его здесь: прогон стартует внутри одного
   * запроса, и до ответа подписываться всё равно не на что.
   */
  async regenerateSelectedVisuals(
    designCode: string,
    payload: {
      categoryIds: number[]
      targets: Array<{ marketplace: string; seller_id: number; cabinet?: string }>
      funnelSelections: RegenerationFunnelSelection[]
    },
  ): Promise<RegenerateSelectedVisualsResult> {
    const response = await api.post(
      `/pipeline/${encodeURIComponent(designCode)}/regenerate-visuals`,
      {
        category_ids: payload.categoryIds,
        targets: payload.targets,
        funnel_selections: payload.funnelSelections,
      },
    )
    return unwrap<RegenerateSelectedVisualsResult>(
      response,
      {} as RegenerateSelectedVisualsResult,
    )
  },

  /**
   * Пакетное заведение карточек сайта по выбранным товарам каталога.
   *
   * jobId создаёт фронт и передаёт его бэкенду (как выгрузка на WB): виджет
   * фоновых задач подписывается по нему сразу, не дожидаясь ответа.
   */
  async createSiteCards(
    articles: string[],
    jobId: string,
    cabinet?: string,
  ): Promise<CreateSiteCardsResult> {
    const response = await api.post('/pipeline/site-cards', {
      articles,
      job_id: jobId,
      ...(cabinet ? { cabinet } : {}),
    })
    return unwrap<CreateSiteCardsResult>(response, response.data)
  },

  /** Загрузка своего визуала под товар/кабинет — возвращает { url } */
  async addCustomVisual(article: string, image: string, cabinet: string, format = 'jpg'): Promise<{ url: string }> {
    const response = await api.post(`/pipeline/product/${encodeURIComponent(article)}/visual`, { image, cabinet, format })
    return unwrap<{ url: string }>(response, response.data)
  },

  /** Полная замена списка визуалов товара (удаление/добавление) */
  async setProductImages(article: string, imageUrls: string[]): Promise<any> {
    const response = await api.put(`/products/${encodeURIComponent(article)}`, { image_urls: imageUrls })
    return unwrap(response, response.data)
  },

  async getDesignDetails(designCode: string): Promise<PipelineDesignDetailsDto> {
    const response = await api.get(`/designs/${encodeURIComponent(designCode)}`)
    return unwrap<PipelineDesignDetailsDto>(response, {} as PipelineDesignDetailsDto)
  },

  async updateDesignDetails(
    designCode: string,
    updates: Partial<PipelineDesignDetailsDto>
  ): Promise<PipelineDesignDetailsDto> {
    const response = await api.put(`/designs/${encodeURIComponent(designCode)}`, updates)
    return unwrap<PipelineDesignDetailsDto>(response, {} as PipelineDesignDetailsDto)
  },

  async getCards(params: Record<string, string | number | undefined> = {}): Promise<MarketplaceCardsResponse> {
    const response = await api.get('/cards', { params })
    const data = unwrap<Partial<MarketplaceCardsResponse>>(response, {})
    return {
      items: Array.isArray(data.items) ? data.items : [],
      total: Number(data.total) || 0,
      counts: { by_status: data.counts?.by_status ?? {} },
    }
  },

  /** Exact card/revision read-model for one design; no marketplace writes. */
  async getDesignMarketplaceCards(designCode: string): Promise<MarketplaceCardDto[]> {
    const response = await api.get(
      `/designs/${encodeURIComponent(String(designCode))}/marketplace-cards`,
    )
    const data = unwrap<Partial<MarketplaceCardsResponse>>(response, {})
    return Array.isArray(data.items) ? data.items : []
  },

  async enableCardSales(cardId: number): Promise<any> {
    const response = await api.post(`/cards/${encodeURIComponent(String(cardId))}/enable-sales`, {})
    return unwrap(response, response.data)
  },
}
